import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
	Box,
	Container,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Chip,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Grid,
	CircularProgress,
	Alert,
	IconButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import {
	Search as SearchIcon,
	Download as DownloadIcon,
	FileText as ReceiptIcon,
	Filter as FilterIcon,
	Calendar as CalendarIcon,
} from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";

const MyExpenses = () => {
	const { user, supabase } = useAuth();
	const [expenses, setExpenses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalExpenses, setTotalExpenses] = useState(0);
	const [receiptPreview, setReceiptPreview] = useState({
		open: false,
		url: "",
		description: "",
	});

	// Filter states
	const [filters, setFilters] = useState({
		startDate: null,
		endDate: null,
		expenseType: "",
		searchQuery: "",
		schoolVisitId: "",
		schoolId: "",
		status: "",
	});
	const [showFilters, setShowFilters] = useState(false);
	const [schoolVisits, setSchoolVisits] = useState([]);
	const [schools, setSchools] = useState([]);

	const fetchExpenseData = async () => {
		setLoading(true);
		try {
			// Create query for fetching data with pagination
			let query = supabase
				.from("expenses")
				.select(
					`
          *,
          school_visits (
            id,
            schools (
              id,
              name
            ),
            visited_date,
            planned_date
          ),
          schools (
            id,
            name
          )
        `
				)
				.eq("user_id", user.id)
				.order("date", { ascending: false });

			// Second query for count (simpler, no joins)
			let countQuery = supabase
				.from("expenses")
				.select("id")
				.eq("user_id", user.id);

			// Apply filters to both queries
			if (filters.startDate) {
				query = query.gte("date", filters.startDate.format("YYYY-MM-DD"));
				countQuery = countQuery.gte(
					"date",
					filters.startDate.format("YYYY-MM-DD")
				);
			}

			if (filters.endDate) {
				query = query.lte("date", filters.endDate.format("YYYY-MM-DD"));
				countQuery = countQuery.lte(
					"date",
					filters.endDate.format("YYYY-MM-DD")
				);
			}

			if (filters.expenseType) {
				query = query.eq("expense_type", filters.expenseType);
				countQuery = countQuery.eq("expense_type", filters.expenseType);
			}

			if (filters.searchQuery) {
				query = query.ilike("description", `%${filters.searchQuery}%`);
				countQuery = countQuery.ilike(
					"description",
					`%${filters.searchQuery}%`
				);
			}

			if (filters.status) {
				query = query.eq("status", filters.status);
				countQuery = countQuery.eq("status", filters.status);
			}

			// Only apply UUID filters if they are not empty
			if (filters.schoolVisitId && filters.schoolVisitId.trim() !== "") {
				query = query.eq("school_visit_id", filters.schoolVisitId);
				countQuery = countQuery.eq("school_visit_id", filters.schoolVisitId);
			}

			if (filters.schoolId && filters.schoolId.trim() !== "") {
				query = query.eq("school_id", filters.schoolId);
				countQuery = countQuery.eq("school_id", filters.schoolId);
			}

			// Execute count query first
			const { data: countData, error: countError } = await countQuery;

			if (countError) throw countError;

			// Set total count for pagination
			setTotalExpenses(countData ? countData.length : 0);

			// Then execute data query with pagination
			const { data, error } = await query.range(
				page * rowsPerPage,
				(page + 1) * rowsPerPage - 1
			);

			if (error) throw error;
			setExpenses(data || []);
		} catch (err) {
			console.error("Error fetching expenses:", err);
			setError("Failed to load expenses. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const fetchSchoolVisits = async () => {
		try {
			const { data, error } = await supabase
				.from("school_visits")
				.select(
					`
          id, 
          planned_date, 
          visited_date,
          schools (
            id, 
            name
          )
        `
				)
				.eq("visited_by", user.id)
				.order("visited_date", { ascending: false });

			if (error) throw error;

			// Format the school visits data for the dropdown
			const formattedVisits = data.map((visit) => ({
				id: visit.id,
				schoolName: visit.schools?.name || "Unknown School",
				visitDate: visit.visited_date || visit.planned_date,
				schoolId: visit.schools?.id,
				displayText: `${visit.schools?.name || "Unknown School"} - ${dayjs(
					visit.visited_date || visit.planned_date
				).format("DD/MM/YYYY")}`,
			}));

			setSchoolVisits(formattedVisits);
		} catch (error) {
			console.error("Error fetching school visits:", error);
		}
	};

	const fetchSchools = async () => {
		try {
			const { data, error } = await supabase
				.from("schools")
				.select("id, name")
				.eq("status", "Active")
				.order("name");

			if (error) throw error;
			setSchools(data || []);
		} catch (error) {
			console.error("Error fetching schools:", error);
		}
	};

	useEffect(() => {
		fetchExpenseData();
	}, [page, rowsPerPage, filters]);

	useEffect(() => {
		fetchSchoolVisits();
		fetchSchools();
	}, []);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const resetFilters = () => {
		setFilters({
			startDate: null,
			endDate: null,
			expenseType: "",
			searchQuery: "",
			schoolVisitId: "",
			schoolId: "",
			status: "",
		});
		setPage(0);
	};

	const handleFilterChange = (key, value) => {
		setFilters({
			...filters,
			[key]: value,
		});
		setPage(0);
	};

	const viewReceipt = (receipt, description) => {
		setReceiptPreview({
			open: true,
			url: receipt,
			description: description,
		});
	};

	const closeReceiptPreview = () => {
		setReceiptPreview({
			open: false,
			url: "",
			description: "",
		});
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			minimumFractionDigits: 2,
		}).format(amount);
	};

	const calculateTotalAmount = () => {
		return expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
	};

	const getExpenseTypeChipColor = (type) => {
		switch (type) {
			case "Travel Expense":
				return "primary";
			case "Food":
				return "success";
			case "Room Rent":
				return "secondary";
			case "Bus Fare":
			case "Taxi Fare":
			case "Train Fare":
				return "info";
			case "Toll":
				return "warning";
			default:
				return "default";
		}
	};

	const getStatusChipColor = (status) => {
		switch (status) {
			case "Approved":
				return "success";
			case "Rejected":
				return "error";
			case "Pending Approval":
			default:
				return "warning";
		}
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Container>
				<Typography variant="h4" component="h1" gutterBottom>
					My Expenses
				</Typography>

				{error && (
					<Alert severity="error" sx={{ mb: 3 }}>
						{error}
					</Alert>
				)}

				{/* Filters */}
				<Paper
					elevation={0}
					sx={{
						p: 2,
						mb: 3,
						border: "1px solid",
						borderColor: "divider",
					}}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 2,
						}}>
						<Typography variant="h6">Filters</Typography>
						<Button
							variant="outlined"
							size="small"
							startIcon={<FilterIcon size={16} />}
							onClick={() => setShowFilters(!showFilters)}>
							{showFilters ? "Hide Filters" : "Show Filters"}
						</Button>
					</Box>

					{showFilters && (
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6} md={3}>
								<DatePicker
									label="Start Date"
									value={filters.startDate}
									onChange={(date) => handleFilterChange("startDate", date)}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<DatePicker
									label="End Date"
									value={filters.endDate}
									onChange={(date) => handleFilterChange("endDate", date)}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<FormControl fullWidth size="small">
									<InputLabel>Expense Type</InputLabel>
									<Select
										value={filters.expenseType}
										label="Expense Type"
										onChange={(e) =>
											handleFilterChange("expenseType", e.target.value)
										}>
										<MenuItem value="">All Types</MenuItem>
										<MenuItem value="Travel Expense">Travel Expense</MenuItem>
										<MenuItem value="Food">Food</MenuItem>
										<MenuItem value="Room Rent">Room Rent</MenuItem>
										<MenuItem value="Bus Fare">Bus Fare</MenuItem>
										<MenuItem value="Taxi Fare">Taxi Fare</MenuItem>
										<MenuItem value="Train Fare">Train Fare</MenuItem>
										<MenuItem value="Toll">Toll</MenuItem>
										<MenuItem value="Others">Others</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<FormControl fullWidth size="small">
									<InputLabel>Status</InputLabel>
									<Select
										value={filters.status}
										label="Status"
										onChange={(e) =>
											handleFilterChange("status", e.target.value)
										}>
										<MenuItem value="">All Status</MenuItem>
										<MenuItem value="Pending Approval">
											Pending Approval
										</MenuItem>
										<MenuItem value="Approved">Approved</MenuItem>
										<MenuItem value="Rejected">Rejected</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<TextField
									fullWidth
									size="small"
									label="Search Description"
									value={filters.searchQuery}
									onChange={(e) =>
										handleFilterChange("searchQuery", e.target.value)
									}
									InputProps={{
										startAdornment: (
											<SearchIcon size={18} style={{ marginRight: 8 }} />
										),
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={4}>
								<FormControl fullWidth size="small">
									<InputLabel>School Visit</InputLabel>
									<Select
										value={filters.schoolVisitId}
										label="School Visit"
										onChange={(e) => {
											handleFilterChange("schoolVisitId", e.target.value);
											// Clear school filter if school visit is selected
											if (e.target.value) {
												handleFilterChange("schoolId", "");
											}
										}}>
										<MenuItem value="">All School Visits</MenuItem>
										{schoolVisits.map((visit) => (
											<MenuItem key={visit.id} value={visit.id}>
												{visit.displayText}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6} md={5}>
								<FormControl fullWidth size="small">
									<InputLabel>School</InputLabel>
									<Select
										value={filters.schoolId}
										label="School"
										onChange={(e) => {
											handleFilterChange("schoolId", e.target.value);
											// Clear school visit filter if school is selected
											if (e.target.value) {
												handleFilterChange("schoolVisitId", "");
											}
										}}>
										<MenuItem value="">All Schools</MenuItem>
										{schools.map((school) => (
											<MenuItem key={school.id} value={school.id}>
												{school.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
									<Button
										variant="outlined"
										onClick={resetFilters}
										sx={{ mr: 1 }}>
										Reset Filters
									</Button>
									<Button
										variant="contained"
										onClick={() => fetchExpenseData()}>
										Apply Filters
									</Button>
								</Box>
							</Grid>
						</Grid>
					)}
				</Paper>

				{/* Summary Box */}
				<Paper
					elevation={0}
					sx={{
						p: 2,
						mb: 3,
						bgcolor: "primary.light",
						color: "primary.contrastText",
						borderRadius: 1,
					}}>
					<Grid container alignItems="center">
						<Grid item xs={12} md={8}>
							<Typography variant="body1" fontWeight="medium">
								Total Expenses: {totalExpenses}{" "}
								{totalExpenses === 1 ? "item" : "items"}
							</Typography>
							{loading && (
								<Typography variant="caption">Loading expenses...</Typography>
							)}
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="h5" align="right" fontWeight="bold">
								{formatCurrency(calculateTotalAmount())}
							</Typography>
						</Grid>
					</Grid>
				</Paper>

				{/* Expenses Table */}
				<Paper
					elevation={0}
					sx={{
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 1,
						overflow: "hidden",
					}}>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ bgcolor: "grey.100" }}>
									<TableCell>Date</TableCell>
									<TableCell>Description</TableCell>
									<TableCell>Type</TableCell>
									<TableCell align="right">Amount</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>School/Visit</TableCell>
									<TableCell>Receipt</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 3 }}>
											<CircularProgress size={30} />
										</TableCell>
									</TableRow>
								) : expenses.length === 0 ? (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 3 }}>
											<Typography color="text.secondary">
												No expenses found
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									expenses.map((expense) => (
										<TableRow key={expense.id}>
											<TableCell>
												<Box sx={{ display: "flex", alignItems: "center" }}>
													<CalendarIcon size={16} style={{ marginRight: 8 }} />
													{dayjs(expense.date).format("DD/MM/YYYY")}
												</Box>
											</TableCell>
											<TableCell>{expense.description}</TableCell>
											<TableCell>
												<Chip
													label={expense.expense_type}
													size="small"
													color={getExpenseTypeChipColor(expense.expense_type)}
													variant="outlined"
												/>
												{expense.distance && (
													<Typography variant="caption" display="block">
														{expense.distance} KM
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												{formatCurrency(expense.amount)}
											</TableCell>
											<TableCell>
												<Chip
													label={expense.status || "Pending Approval"}
													size="small"
													color={getStatusChipColor(expense.status)}
													variant="outlined"
												/>
											</TableCell>
											<TableCell>
												{expense.school_visits ? (
													<Tooltip title="School Visit">
														<Chip
															size="small"
															label={`${
																expense.school_visits.schools?.name || "Unknown"
															} (Visit)`}
															color="info"
															variant="outlined"
														/>
													</Tooltip>
												) : expense.schools ? (
													<Tooltip title="School">
														<Chip
															size="small"
															label={expense.schools.name}
															color="default"
															variant="outlined"
														/>
													</Tooltip>
												) : (
													"-"
												)}
											</TableCell>
											<TableCell>
												{expense.receipt_url ? (
													<IconButton
														size="small"
														onClick={() =>
															viewReceipt(
																expense.receipt_url,
																expense.description
															)
														}>
														<ReceiptIcon size={16} />
													</IconButton>
												) : (
													<Typography variant="caption" color="text.secondary">
														No receipt
													</Typography>
												)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={totalExpenses}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Paper>

				{/* Receipt Preview Dialog */}
				<Dialog
					open={receiptPreview.open}
					onClose={closeReceiptPreview}
					maxWidth="md"
					fullWidth>
					<DialogTitle>Receipt: {receiptPreview.description}</DialogTitle>
					<DialogContent>
						{receiptPreview.url.toLowerCase().endsWith(".pdf") ? (
							<Box sx={{ height: "70vh", width: "100%" }}>
								<iframe
									src={receiptPreview.url}
									style={{ width: "100%", height: "100%", border: "none" }}
									title="Receipt PDF"
								/>
							</Box>
						) : (
							<Box sx={{ textAlign: "center", py: 2 }}>
								<img
									src={receiptPreview.url}
									alt="Receipt"
									style={{ maxWidth: "100%", maxHeight: "70vh" }}
								/>
							</Box>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							startIcon={<DownloadIcon size={16} />}
							href={receiptPreview.url}
							target="_blank"
							rel="noopener">
							Download
						</Button>
						<Button onClick={closeReceiptPreview}>Close</Button>
					</DialogActions>
				</Dialog>
			</Container>
		</LocalizationProvider>
	);
};

export default MyExpenses;
